import React, { useState, useEffect, useMemo } from 'react';
import './Profile.css';
import { useAuth } from '../../AuthContext';
import { apiUrl } from '../../api/client';
import SiteHeader from '../../shared/SiteHeader';
import { isErrorResponse } from '../../types/ErrorResponse';
import { PasswordInput } from '../../shared/PasswordInput';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setUserProfile } from '../../store/userProfileSlice';
import { createApiClient } from '../../api/ApiClient';
import { UserProfile } from '../../types/UserProfile';
import { ApiRoutes } from '../../types/ApiRoutes';

const Profile: React.FC = () => {
  const { authorizedFetch } = useAuth();
  const api = useMemo(() => createApiClient(authorizedFetch), [authorizedFetch]);
  const dispatch = useAppDispatch();
  const userProfile = useAppSelector((state) => state.userProfile.profile);
  const userProfileLoaded = useAppSelector((state) => state.userProfile.loaded);

  const [name, setName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [nameError, setNameError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [nameSuccessMessage, setNameSuccessMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);

  // Load user profile on mount
  useEffect(() => {
    const loadProfile = async () => {
      if (userProfileLoaded) {
        setName(userProfile?.properties['name'] || '');
        return;
      }

      try {
        setLoadingProfile(true);
        const profile = await api.get<UserProfile>(apiUrl(ApiRoutes.UserProfile), {
          errorMessage: 'Failed to load user profile',
        });
        dispatch(setUserProfile(profile));
        setName(profile.properties['name'] || '');
      } catch (err: unknown) {
        console.error('Failed to load user profile:', err);
      } finally {
        setLoadingProfile(false);
      }
    };

    void loadProfile();
  }, [userProfileLoaded, userProfile, api, dispatch]);

  const isStrongPassword = (value: string) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).{8,}$/.test(value);

  const handleNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNameError('');
    setNameSuccessMessage('');

    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError('Name cannot be empty.');
      return;
    }

    setSavingName(true);

    try {
      const profileToSave = {
        ...userProfile,
        properties: {
          ...userProfile?.properties,
          name: trimmedName,
        },
      };

      const updatedProfile = await api.post<UserProfile>(
        apiUrl(ApiRoutes.UserProfile),
        profileToSave,
        { errorMessage: 'Failed to update profile' }
      );

      dispatch(setUserProfile(updatedProfile));
      setNameSuccessMessage('Name updated successfully!');
      setTimeout(() => setNameSuccessMessage(''), 3000);
    } catch (err: unknown) {
      if (isErrorResponse(err)) {
        setNameError(err.message);
      } else {
        setNameError('An error occurred while updating your name');
      }
    } finally {
      setSavingName(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (!isStrongPassword(newPassword)) {
      setError(
        'Password must be at least 8 characters and include an uppercase letter, lowercase letter, number, and symbol.'
      );
      return;
    }

    setSaving(true);

    try {
      const res = await authorizedFetch(apiUrl(ApiRoutes.ChangePassword), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPassword }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Failed to change password');
      }

      setSuccessMessage('Password changed successfully!');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: unknown) {
      if (isErrorResponse(err)) {
        setError(err.message);
      } else {
        setError('An error occurred while changing the password');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SiteHeader />
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10">
            <h2 className="mb-2">User Profile</h2>
            <p className="text-muted mb-4">Manage your account settings and preferences.</p>
          </div>
        </div>

        <div className="row justify-content-center mb-3">
          {/* Name Section */}
          <div className="col-12 col-lg-5">
            <div className="card h-100">
              <div className="card-body">
                <h5 className="card-title mb-2">Name</h5>
                <p className="text-muted mb-4">
                  Walk It With Me will use your name to personalize your experience.
                </p>

                {loadingProfile ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <>
                    {nameError && (
                      <div className="alert alert-danger" role="alert">
                        {nameError}
                      </div>
                    )}

                    {nameSuccessMessage && (
                      <div className="alert alert-success" role="alert">
                        {nameSuccessMessage}
                      </div>
                    )}

                    <form onSubmit={handleNameSubmit}>
                      <div className="mb-3">
                        <label htmlFor="userName" className="form-label">
                          Your Name
                        </label>
                        <input
                          id="userName"
                          type="text"
                          className="form-control"
                          placeholder="Enter your name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          required
                        />
                      </div>
                      <div className="text-muted">
                        <small>
                          <em>
                            You can change your name here or on the Chat page by saying, "My name is
                            [Your Name]".
                          </em>
                        </small>
                      </div>
                    </form>
                  </>
                )}
              </div>
              {!loadingProfile && (
                <div className="card-footer">
                  <div className="d-grid">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={savingName}
                      onClick={handleNameSubmit}
                    >
                      {savingName ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Saving...
                        </>
                      ) : (
                        'Save Name'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Change Password Section */}
          <div className="col-12 col-lg-5">
            <div className="card h-100">
              <div className="card-body">
                <h5 className="card-title mb-2">Change Password</h5>
                <p className="text-muted mb-4">Update your password to keep your account secure.</p>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}

                {successMessage && (
                  <div className="alert alert-success" role="alert">
                    {successMessage}
                  </div>
                )}

                <form onSubmit={handlePasswordSubmit}>
                  <PasswordInput
                    id="newPassword"
                    label="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).{8,}"
                    title="At least 8 characters with uppercase, lowercase, number, and symbol"
                    autoComplete="new-password"
                  />
                  <PasswordInput
                    id="confirmPassword"
                    label="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).{8,}"
                    title="Must match and meet password complexity"
                    autoComplete="new-password"
                  />
                </form>
              </div>
              <div className="card-footer">
                <div className="d-grid">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={saving}
                    onClick={handlePasswordSubmit}
                  >
                    {saving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Changing Password...
                      </>
                    ) : (
                      'Change Password'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row justify-content-center">
          {/* Profile Properties Section */}
          <div className="col-12 col-lg-5">
            <div className="card h-100">
              <div className="card-body">
                <h5 className="card-title mb-2">Profile Properties</h5>
                <p className="text-muted mb-4">
                  Below are the properties Walk It With Me has stored based on your chat messages.
                </p>

                {loadingProfile ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : userProfile ? (
                  <dl>
                    {Object.entries(userProfile.properties)
                      .filter(([key]) => key !== 'name')
                      .map(([key, value]) => (
                        <React.Fragment key={key}>
                          <dt>{key}</dt>
                          <dd>
                            {value !== null && value !== undefined ? (
                              String(value)
                            ) : (
                              <span className="text-muted">Not set</span>
                            )}
                          </dd>
                        </React.Fragment>
                      ))}
                  </dl>
                ) : (
                  <div className="alert alert-info" role="alert">
                    No profile data available.
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Placeholder Section */}
          <div className="col-12 col-lg-5"></div>
        </div>
      </div>
    </>
  );
};

export default Profile;
